import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Icon, type IconName } from './Icon';
import { ORG_SECTIONS, orgPath, WORKSPACE_SECTIONS, workspacePath, useNav, type NavOrg } from '../nav';
import { useSession } from '../session';

function Item({ to, icon, label, end = true, active }: { to: string; icon: IconName; label: string; end?: boolean; active?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => ((active ?? isActive) ? 'nav-link active' : 'nav-link')}
    >
      <Icon name={icon} />
      <span>{label}</span>
    </NavLink>
  );
}

const CONSOLE_SECTIONS: { to: string; label: string; icon: IconName }[] = [
  { to: '/admin/keys', label: 'Signing keys', icon: 'key' },
  { to: '/admin/workspaces', label: 'Workspaces', icon: 'people' },
  { to: '/admin/errors', label: 'Errors', icon: 'log' },
  { to: '/admin/invites', label: 'Invite codes', icon: 'plus' },
];

const envTag = (environment: string) => (environment === 'sandbox' ? 'Sandbox' : 'Production');

/**
 * The one menu.
 *
 * It reads top to bottom the way the product is put together: a workspace is the company, the company
 * connects Salesforce orgs, and each org has its own users, grants and logs. The open org unfolds in
 * place under its name, so there is never a question of which org a "Settings" link belongs to.
 */
export function Sidebar({ onNavigate }: { onNavigate: () => void }) {
  const { me, productName, signOut } = useSession();
  const { tenantId, orgId, orgs } = useNav();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (!me) return null;

  const membership = me.memberships.find((m) => m.tenantId === tenantId);
  const canManage = membership?.role === 'owner' || membership?.role === 'admin';
  const connecting = tenantId !== null && pathname === `/workspaces/${tenantId}/orgs/new`;

  return (
    // A click anywhere in the menu that lands on a link closes the drawer on a phone. On a wide screen
    // there is no drawer and this does nothing.
    <aside id="app-menu" className="sidebar" onClick={(e) => (e.target as HTMLElement).closest('a') && onNavigate()}>
      <Link to="/" className="brand">{productName}</Link>

      <nav className="nav-group" aria-label="Main">
        <Item to="/" icon="home" label="Home" />
        <Item to="/open" icon="open" label="Open an org" />
      </nav>

      {membership && tenantId && (
        <nav className="nav-group" aria-label="Workspace">
          <div className="nav-heading">Workspace</div>
          {me.memberships.length > 1 ? (
            <select
              className="ws-switch"
              aria-label="Switch workspace"
              value={tenantId}
              onChange={(e) => {
                navigate(workspacePath(e.target.value));
                onNavigate();
              }}
            >
              {me.memberships.map((m) => (
                <option key={m.tenantId} value={m.tenantId}>{m.name}</option>
              ))}
            </select>
          ) : (
            <div className="ws-name">{membership.name}</div>
          )}
          {WORKSPACE_SECTIONS.filter((s) => !s.manage || canManage).map((s) => (
            <Item key={s.path} to={workspacePath(tenantId, s.path)} icon={s.icon} label={s.label} />
          ))}
        </nav>
      )}

      {membership && tenantId && (
        <nav className="nav-group" aria-label="Salesforce orgs">
          <div className="nav-heading">Salesforce orgs</div>
          {orgs === null && <div className="nav-note">Loading…</div>}
          {orgs?.length === 0 && (
            <div className="nav-note">{canManage ? 'None connected yet.' : 'You hold no grants here yet.'}</div>
          )}
          {orgs?.map((o) => (
            <OrgEntry key={o.id} tenantId={tenantId} org={o} open={o.id === orgId} canManage={canManage} />
          ))}
          {canManage && (
            <Link to={`/workspaces/${tenantId}/orgs/new`} className={connecting ? 'nav-link nav-add active' : 'nav-link nav-add'}>
              <Icon name="plus" />
              <span>Connect an org</span>
            </Link>
          )}
        </nav>
      )}

      <div className="nav-foot">
        <Item to="/mfa-resets" icon="shield" label="Reset requests" />
        {me.isOperator && <Item to="/admin" icon="console" label="Console" end={false} />}
        {me.isOperator && pathname.startsWith('/admin') && (
          <div className="nav-sub">
            {CONSOLE_SECTIONS.map((s) => (
              <Item key={s.to} to={s.to} icon={s.icon} label={s.label} />
            ))}
          </div>
        )}
        <Item to="/account" icon="user" label="Your account" />
        <div className="nav-me">
          <div className="nav-me-name">{me.user.displayName || me.user.email}</div>
          {me.user.displayName && <div className="nav-me-email">{me.user.email}</div>}
        </div>
        <button type="button" className="secondary block" onClick={() => signOut().then(() => navigate('/login'))}>
          <Icon name="signout" />
          Sign out
        </button>
      </div>
    </aside>
  );
}

function OrgEntry({ tenantId, org, open, canManage }: { tenantId: string; org: NavOrg; open: boolean; canManage: boolean }) {
  const { pathname } = useLocation();
  const base = orgPath(tenantId, org.id);
  // The skill editor sits outside the org's own pages, so it lights up Skills by hand.
  const inSkill = pathname.startsWith(`${base}/skills/`);
  return (
    <div className={open ? 'nav-org open' : 'nav-org'}>
      <NavLink to={base} end={false} className={open ? 'nav-link nav-org-name active' : 'nav-link nav-org-name'}>
        <Icon name="cloud" />
        <span className="nav-org-label">{org.label}</span>
        <span className={`env-tag ${org.environment}`}>{envTag(org.environment)}</span>
        {org.status !== 'active' && <span className={`dot ${org.status}`} title={org.status} />}
      </NavLink>
      {open && (
        <div className="nav-sub">
          {ORG_SECTIONS.filter((s) => !s.manage || canManage).map((s) => (
            <Item
              key={s.path}
              to={orgPath(tenantId, org.id, s.path)}
              icon={s.icon}
              label={s.label}
              active={s.path === 'skills' && inSkill ? true : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
